'use client'

import * as React from 'react'
import { Plus, Trash2, Users } from 'lucide-react'
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { toast } from 'sonner'
import { db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

export default function ProjectTeamPage() {
  const { project, setProject } = useProject()
  const { canEdit } = useUser()
  const [availableUsers, setAvailableUsers] = React.useState([])
  const [selectedMemberId, setSelectedMemberId] = React.useState('')
  const [savingTeam, setSavingTeam] = React.useState(false)
  const [teamForm, setTeamForm] = React.useState({
    principalInvestigatorId: project.principalInvestigatorId || '',
    principalInvestigatorName: project.principalInvestigatorName || '',
    projectManagerId: project.projectManagerId || '',
    projectManagerName: project.projectManagerName || '',
    projectMembers: Array.isArray(project.projectMembers) ? project.projectMembers : [],
  })

  const getUserLabel = React.useCallback(
    (user) => user?.fullName || user?.name || user?.email || 'User',
    [],
  )

  React.useEffect(() => {
    let isMounted = true

    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setAvailableUsers(records)
      } catch {
        setAvailableUsers([])
      }
    }

    loadUsers()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    setTeamForm({
      principalInvestigatorId: project.principalInvestigatorId || '',
      principalInvestigatorName: project.principalInvestigatorName || '',
      projectManagerId: project.projectManagerId || '',
      projectManagerName: project.projectManagerName || '',
      projectMembers: Array.isArray(project.projectMembers) ? project.projectMembers : [],
    })
    setSelectedMemberId('')
  }, [project.id])

  const addProjectMember = () => {
    if (!selectedMemberId) return
    const selected = availableUsers.find((user) => (user.userId || user.id) === selectedMemberId)
    if (!selected) return
    const memberId = selected.userId || selected.id
    setTeamForm((prev) => {
      if (prev.projectMembers.some((member) => member.id === memberId)) {
        return prev
      }
      return {
        ...prev,
        projectMembers: [
          ...prev.projectMembers,
          {
            id: memberId,
            name: getUserLabel(selected),
          },
        ],
      }
    })
    setSelectedMemberId('')
  }

  const removeProjectMember = (memberId) => {
    setTeamForm((prev) => ({
      ...prev,
      projectMembers: prev.projectMembers.filter((member) => member.id !== memberId),
    }))
  }

  const handleSaveTeam = async () => {
    if (!canEdit) return
    if (!project?.id) return
    setSavingTeam(true)
    try {
      const payload = {
        principalInvestigatorId: teamForm.principalInvestigatorId || '',
        principalInvestigatorName: teamForm.principalInvestigatorName || '',
        projectManagerId: teamForm.projectManagerId || '',
        projectManagerName: teamForm.projectManagerName || '',
        projectMembers: teamForm.projectMembers || [],
        updatedAt: serverTimestamp(),
      }
      await updateDoc(doc(db, 'projects', project.id), payload)
      setProject((prev) => ({ ...prev, ...payload, projectMembers: payload.projectMembers }))
      toast.success('Project team updated')
    } catch (error) {
      toast.error('Failed to update project team', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSavingTeam(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Team & Roles</CardTitle>
              <CardDescription>Assign or update the project PI, manager, and members.</CardDescription>
            </div>
            <Badge variant="secondary" className="gap-1">
              <Users className="size-3.5" />
              {teamForm.projectMembers.length} members
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {!canEdit ? (
            <p className="text-xs text-muted-foreground mb-3">
              You do not have permission to edit the project team.
            </p>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Principal Investigator</Label>
              <Select
                value={teamForm.principalInvestigatorId}
                onValueChange={(value) => {
                  const selected = availableUsers.find((user) => (user.userId || user.id) === value)
                  setTeamForm((prev) => ({
                    ...prev,
                    principalInvestigatorId: value,
                    principalInvestigatorName: getUserLabel(selected),
                  }))
                }}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select principal investigator" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="no-users" disabled>
                      No users available
                    </SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                        {getUserLabel(user)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Project Manager</Label>
              <Select
                value={teamForm.projectManagerId}
                onValueChange={(value) => {
                  const selected = availableUsers.find((user) => (user.userId || user.id) === value)
                  setTeamForm((prev) => ({
                    ...prev,
                    projectManagerId: value,
                    projectManagerName: getUserLabel(selected),
                  }))
                }}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project manager" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="no-users" disabled>
                      No users available
                    </SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                        {getUserLabel(user)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label>Project Members</Label>
            <div className="flex gap-2">
              <Select value={selectedMemberId} onValueChange={setSelectedMemberId} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="no-users" disabled>
                      No users available
                    </SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                        {getUserLabel(user)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addProjectMember}
                disabled={!canEdit || !selectedMemberId}
              >
                <Plus className="mr-1 size-3.5" /> Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {teamForm.projectMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground">No members added yet.</p>
              ) : (
                teamForm.projectMembers.map((member) => (
                  <div
                    key={member.id || member.name}
                    className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-foreground"
                  >
                    <span>{member.name || 'Member'}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-5"
                      onClick={() => removeProjectMember(member.id)}
                      disabled={!canEdit}
                    >
                      <Trash2 className="size-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleSaveTeam} disabled={!canEdit || savingTeam}>
              {savingTeam ? 'Saving...' : 'Save Team'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
